import { useState, useCallback } from 'react';
import { leadsApi } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { LeadSource } from '../../types/lead';
import { isValidPhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js/max';
import { parseCustomFields } from '../../utils/leads/leadsUtils';

interface UseLeadsProps {
  smbId?: string;
  onSuccess?: (newLead: any, httpStatus: number) => void;
  setLeads?: React.Dispatch<React.SetStateAction<any[]>>;
  fetchLeads?: (isRefresh?: boolean) => void;
}

export type CreateLeadResult = 
  | { status: 'success'; lead: any }
  | { status: 'conflict'; existingLead: any; inputName: string; inputNotes?: string; inputCustomFields?: Record<string, any>; inputStatus?: string }
  | { status: 'error' };

export const useLeads = ({ smbId, onSuccess, setLeads, fetchLeads }: UseLeadsProps = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const { showNotification } = useNotification();

  const createLead = useCallback(
    async (
      name: string, 
      phone: string, 
      source: LeadSource = LeadSource.DIRECT,
      status: string = 'New',
      notes?: string,
      customFields?: Record<string, any>
    ): Promise<CreateLeadResult> => {
      if (!smbId) {
        setServerError('SMB ID is required');
        return { status: 'error' };
      }

      const trimmedName = name.trim();
      const trimmedPhone = phone.trim();
      const trimmedNotes = notes ? notes.trim() : undefined;
      
      setNameError(null);
      setPhoneError(null);
      setServerError(null);

      let hasError = false;

      if (trimmedName) {
        if (trimmedName.length > 510) {
          setNameError('Name is too long');
          hasError = true;
        }
      }

      // Phone Validation
      if (!trimmedPhone) {
        setPhoneError('Phone number is required.');
        hasError = true;
      } else {
        if (!isValidPhoneNumber(trimmedPhone)) {
          setPhoneError('Please enter a valid phone number.');
          hasError = true;
        }
      }

      if (hasError) {
        return { status: 'error' };
      }

      // Convert to E.164 and remove the + sign before sending to backend
      const parsedPhoneWithPlus = parsePhoneNumberWithError(trimmedPhone)?.format('E.164') || trimmedPhone;
      const parsedPhone = parsedPhoneWithPlus.startsWith('+') ? parsedPhoneWithPlus.substring(1) : parsedPhoneWithPlus;

      setIsLoading(true);
      try {
        const { status: httpStatus, data: created } = await leadsApi.createLead(smbId, {
          name: trimmedName,
          phone: parsedPhone,
          source: source,
          lead_status: status,
          notes: trimmedNotes,
          custom_fields: customFields,
        });

        const createdAny = created as any;
        const newLead = {
          ...created,
          id: String(created.id),
          name: created.name || '',
          status: created.lead_status || createdAny?.status || status,
          notes: created.notes ?? createdAny?.lead_metadata?.notes ?? trimmedNotes,
          custom_fields: created.custom_fields ?? createdAny?.lead_metadata?.custom_fields ?? customFields,
        };

        if (httpStatus === 201) {
          showNotification('Lead created successfully', 'success');
          if (onSuccess) {
            onSuccess(newLead, httpStatus);
          }
          return { status: 'success', lead: newLead };
        } else {
          // 200 → backend returned an existing lead (idempotent)
          return { status: 'conflict', existingLead: newLead, inputName: trimmedName, inputNotes: trimmedNotes, inputCustomFields: customFields, inputStatus: status };
        }
      } catch (err: any) {
        console.error('Create lead failed:', err);
        const detail = err?.response?.data?.detail;
        let msg = 'Failed to create lead. Please try again.';
        if (typeof detail === 'string') {
          msg = detail;
        } else if (Array.isArray(detail) && detail.length > 0 && detail[0].msg) {
          msg = detail[0].msg.replace('Value error, ', '');
        }
        setServerError(msg);
        return { status: 'error' };
      } finally {
        setIsLoading(false);
      }
    },
    [smbId, onSuccess, showNotification]
  );

  const handleStatusChange = useCallback(
    async (
      leadId: string,
      newStatus: string,
      overrideSetLeads?: React.Dispatch<React.SetStateAction<any[]>>,
      overrideFetchLeads?: (isRefresh?: boolean) => void
    ) => {
      const activeSetLeads = overrideSetLeads || setLeads;
      const activeFetchLeads = overrideFetchLeads || fetchLeads;

      if (activeSetLeads) {
        activeSetLeads((prevLeads) =>
          prevLeads.map((lead) =>
            lead.id === leadId ? { ...lead, status: newStatus } : lead
          )
        );
      }
      try {
        await leadsApi.updateLead(leadId, { lead_status: newStatus });
        showNotification('Lead status updated successfully', 'success');
      } catch (err) {
        showNotification('Failed to update status', 'error');
        if (activeFetchLeads) {
          activeFetchLeads();
        }
      }
    },
    [setLeads, fetchLeads, showNotification]
  );

  const handleSourceChange = useCallback(
    async (
      leadId: string,
      newSource: string,
      overrideSetLeads?: React.Dispatch<React.SetStateAction<any[]>>,
      overrideFetchLeads?: (isRefresh?: boolean) => void
    ) => {
      const activeSetLeads = overrideSetLeads || setLeads;
      const activeFetchLeads = overrideFetchLeads || fetchLeads;

      if (activeSetLeads) {
        activeSetLeads((prevLeads) =>
          prevLeads.map((lead) =>
            lead.id === leadId ? { ...lead, source: newSource } : lead
          )
        );
      }
      try {
        await leadsApi.updateLead(leadId, { source: newSource });
        showNotification('Lead source updated successfully', 'success');
      } catch (err) {
        showNotification('Failed to update source', 'error');
        if (activeFetchLeads) {
          activeFetchLeads();
        }
      }
    },
    [setLeads, fetchLeads, showNotification]
  );

  const handleCustomFieldChange = useCallback(
    async (
      leadId: string,
      fieldKey: string,
      newValue: any,
      overrideSetLeads?: React.Dispatch<React.SetStateAction<any[]>>,
      overrideFetchLeads?: (isRefresh?: boolean) => void
    ) => {
      const activeSetLeads = overrideSetLeads || setLeads;
      const activeFetchLeads = overrideFetchLeads || fetchLeads;

      if (activeSetLeads) {
        activeSetLeads((prevLeads) =>
          prevLeads.map((lead) => {
            if (lead.id === leadId) {
              const updatedCustomFields = {
                ...parseCustomFields(lead.custom_fields),
                [fieldKey]: newValue,
              };
              return { ...lead, custom_fields: updatedCustomFields };
            }
            return lead;
          })
        );
      }
      try {
        await leadsApi.updateLead(leadId, {
          custom_fields: { [fieldKey]: newValue },
        });
        showNotification('Lead updated successfully', 'success');
      } catch (err) {
        showNotification('Failed to update lead', 'error');
        if (activeFetchLeads) {
          activeFetchLeads();
        }
      }
    },
    [setLeads, fetchLeads, showNotification]
  );

  return {
    createLead,
    handleStatusChange,
    handleSourceChange,
    handleCustomFieldChange,
    isLoading,
    nameError,
    phoneError,
    serverError,
    setNameError,
    setPhoneError,
    setServerError,
  };
};

